import type { ReactNode } from "react";

/**
 * One panel of the one-screen layout (audits/2026-10-29/gui/principles.md §3): one
 * subject, its actions in its own header, and a body that shares the page's height
 * with the panels beside it.
 *
 * 🔴 The body scrolls when it must, rather than clipping. The page does not scroll
 * (`Page fill`); a panel whose rows outgrow it should page them (`Table pageSize`), but
 * a body that hid its overflow would hide exactly what must be seen without a click — a
 * banner about the state X reports. A scrolling panel is a layout to fix; a clipped
 * warning is a console that says less than it knows.
 *
 * 🔴 As tall as what it holds, up to the room it is given (`h-fit max-h-full`): empty room
 * under two rows is not the panel's to take (3d, 30.10 — Agents, Approvals). A screen that
 * wants a panel to fill its column says so (`flex-1`); a grid cell no longer stretches it.
 */
export interface PanelProps {
	/** The panel's subject, in the header. */
	title: ReactNode;
	/** Beside the title: an id, a status badge, what X reports. */
	aside?: ReactNode;
	/** The panel's own actions, at the right of its header. */
	actions?: ReactNode;
	/** A line under the body: counts, Load more. */
	footer?: ReactNode;
	className?: string;
	bodyClassName?: string;
	children?: ReactNode;
}

export function Panel({
	title,
	aside,
	actions,
	footer,
	className = "",
	bodyClassName = "",
	children,
}: PanelProps) {
	return (
		<section
			className={`flex h-fit max-h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-line bg-surface ${className}`.trim()}
		>
			<header className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-line px-3 py-2">
				<h2 className="text-xs font-semibold uppercase tracking-wider text-muted">{title}</h2>
				{aside}
				{actions != null && (
					<div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>
				)}
			</header>
			<div
				className={`flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-2 ${bodyClassName}`.trim()}
			>
				{children}
			</div>
			{footer != null && (
				<footer className="flex flex-shrink-0 flex-wrap items-center gap-2 border-t border-line px-3 py-1.5 text-xs text-muted">
					{footer}
				</footer>
			)}
		</section>
	);
}
